/**
 * PartsCounterReportModal
 *
 * Consolidated-only modal for the Parts Counterperson Performance report
 * (Reports → Parts at Consolidated). Month-range picker (default YTD) over the
 * monthly PARTS_COUNTER DynamoDB records — numbers tie exactly to the Forecast
 * card; the current month is MTD-as-of-last-nightly-load.
 *
 * Mirrors the TradeInReportModal shell (status states, retry-on-network-error,
 * print layout, ReportExportButtons footer) but always runs at locationId=0
 * and uses the month-range selector instead of the date-range selector.
 */

import React, { useState } from 'react';
import { XMarkIcon, PlayIcon, CircleStackIcon } from '@heroicons/react/24/outline';
import { ReportDefinition } from '../../types/reports';
import { ReportExportButtons } from './ReportExportButtons';
import {
  ReportMonthRangeSelector,
  getDefaultStartMonth,
  getDefaultEndMonth,
  monthSpan,
  monthRangeToStartDate,
  monthRangeToEndDate,
} from './ReportMonthRangeSelector';
import { printCurrentPage } from '../../utils/reportExport';
import { loadExcelJS } from '../../utils/loadExcelJS';
import { runReport, RunReportResponse } from '../../services/api';
import {
  PartsCounterReport,
  PartsCounterReportData,
  PAY_TYPE_LABELS,
  getReportLocationName,
  formatMonthLabel,
} from './renderers/PartsCounterReport';

interface PartsCounterReportModalProps {
  report: ReportDefinition;
  isOpen: boolean;
  onClose: () => void;
}

type ReportStatus = 'idle' | 'running' | 'complete' | 'error';

export const PartsCounterReportModal: React.FC<PartsCounterReportModalProps> = ({
  report,
  isOpen,
  onClose,
}) => {
  const [startMonth, setStartMonth] = useState(getDefaultStartMonth);
  const [endMonth, setEndMonth] = useState(getDefaultEndMonth);

  const [status, setStatus] = useState<ReportStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<RunReportResponse | null>(null);
  const [runningMessage, setRunningMessage] = useState('Generating report...');

  const span = monthSpan(startMonth, endMonth);
  const isValidRange = span >= 1 && span <= 12;

  const isRetryableError = (err: unknown): boolean => {
    const msg = err instanceof Error ? err.message.toLowerCase() : '';
    return msg.includes('failed to fetch') || msg.includes('networkerror') ||
      msg.includes('timeout') || msg.includes('504') || msg.includes('503') ||
      msg.includes('network') || msg.includes('aborted') || msg.includes('timed out');
  };

  const handleRunReport = async () => {
    if (!isValidRange) return;

    setStatus('running');
    setError(null);
    setReportData(null);
    setRunningMessage('Generating report...');

    const warmupTimer = setTimeout(() => {
      setRunningMessage('Reading parts counter data...');
    }, 5000);

    const MAX_ATTEMPTS = 2;
    let lastError: unknown;

    try {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const response = await runReport(
            report.id,
            0, // consolidated-only report
            monthRangeToStartDate(startMonth),
            monthRangeToEndDate(endMonth)
          );

          if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to generate report');
          }

          clearTimeout(warmupTimer);
          setReportData(response.data);
          setStatus('complete');
          return;
        } catch (err) {
          lastError = err;
          if (attempt < MAX_ATTEMPTS && isRetryableError(err)) {
            console.warn(`Parts Counter report attempt ${attempt} failed, retrying...`, err);
            setRunningMessage('Retrying...');
            continue;
          }
          throw err;
        }
      }
    } catch {
      clearTimeout(warmupTimer);
      setError(lastError instanceof Error ? lastError.message : 'Failed to generate report');
      setStatus('error');
    }
  };

  const handlePrintPDF = () => {
    printCurrentPage();
  };

  const handleExportExcel = async () => {
    if (!reportData?.data) return;
    const data = reportData.data as PartsCounterReportData;
    const rangeLabel = data.startMonth === data.endMonth
      ? formatMonthLabel(data.startMonth)
      : `${formatMonthLabel(data.startMonth)} to ${formatMonthLabel(data.endMonth)}`;

    const ExcelJS = await loadExcelJS();
    const wb = new ExcelJS.Workbook();
    wb.creator = "Darling's Dashboard";
    wb.created = new Date();

    const headerStyle = (cell: any, rightAlign: boolean) => {
      cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF374151' } };
      cell.alignment = { horizontal: rightAlign ? 'right' : 'left', vertical: 'middle' };
    };
    const money = '"$"#,##0';
    const pct = '0.0"%"';

    // ── Sheet 1: Summary (per-person totals grouped by location) ──
    const ws = wb.addWorksheet('Summary', {
      views: [{ state: 'frozen', xSplit: 0, ySplit: 5 }],
      pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
    });

    const HEADERS = ['Location', 'Rank', 'Counterperson', 'Gross', 'Sales', 'Cost', 'GP%', 'Transactions', 'Lost Sales'];
    const lastCol = ws.getColumn(HEADERS.length).letter;

    ws.getCell('A1').value = 'Parts Counterperson Performance — All Locations';
    ws.getCell('A1').font = { size: 16, bold: true, color: { argb: 'FF1F2937' } };
    ws.mergeCells(`A1:${lastCol}1`);
    ws.getCell('A2').value = `${rangeLabel} · ${data.companySummary.personCount} counterpeople`;
    ws.getCell('A2').font = { size: 11, color: { argb: 'FF4B5563' } };
    ws.mergeCells(`A2:${lastCol}2`);
    ws.getCell('A3').value = `Generated: ${new Date().toLocaleString('en-US')}`;
    ws.getCell('A3').font = { size: 10, italic: true, color: { argb: 'FF6B7280' } };
    ws.mergeCells(`A3:${lastCol}3`);

    const headerRow = ws.getRow(5);
    HEADERS.forEach((h, i) => {
      const cell = headerRow.getCell(i + 1);
      cell.value = h;
      headerStyle(cell, i >= 3);
    });
    headerRow.height = 20;

    let rowIdx = 6;
    const writePersonRow = (r: any, values: any[], bold = false) => {
      r.values = values;
      r.getCell(4).numFmt = money;
      r.getCell(5).numFmt = money;
      r.getCell(6).numFmt = money;
      r.getCell(7).numFmt = pct;
      for (let c = 4; c <= 9; c++) r.getCell(c).alignment = { horizontal: 'right' };
      if (bold) {
        for (let c = 1; c <= 9; c++) {
          r.getCell(c).font = { bold: true };
          r.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
        }
      }
    };

    for (const location of data.locations) {
      const locationName = getReportLocationName(location.locationId);
      location.persons.forEach((person, i) => {
        writePersonRow(ws.getRow(rowIdx++), [
          locationName,
          i + 1,
          person.employeeName,
          person.grossTotal,
          person.salesTotal,
          person.costTotal,
          person.gpPctTotal ?? '',
          person.transactionCount,
          person.lostSalesCount,
        ]);
      });
      writePersonRow(ws.getRow(rowIdx++), [
        `${locationName} Total`,
        '',
        '',
        location.totalGross,
        location.totalSales,
        location.totalCost,
        location.totalSales !== 0 ? (location.totalGross / location.totalSales) * 100 : '',
        location.totalTransactions,
        location.totalLostSales,
      ], true);
    }
    writePersonRow(ws.getRow(rowIdx++), [
      'All Locations Total',
      '',
      '',
      data.companySummary.totalGross,
      data.companySummary.totalSales,
      data.companySummary.totalCost,
      data.companySummary.totalSales !== 0
        ? (data.companySummary.totalGross / data.companySummary.totalSales) * 100
        : '',
      data.companySummary.totalTransactions,
      data.companySummary.totalLostSales,
    ], true);

    ws.autoFilter = { from: { row: 5, column: 1 }, to: { row: rowIdx - 1, column: HEADERS.length } };
    ws.getColumn(1).width = 32;
    ws.getColumn(2).width = 6;
    ws.getColumn(3).width = 24;
    for (let c = 4; c <= 6; c++) ws.getColumn(c).width = 13;
    ws.getColumn(7).width = 8;
    ws.getColumn(8).width = 13;
    ws.getColumn(9).width = 11;

    // ── Sheet 2: Pay Type Detail (one row per person × pay type) ──
    const wsDetail = wb.addWorksheet('Pay Type Detail', {
      views: [{ state: 'frozen', xSplit: 0, ySplit: 2 }],
      pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
    });

    const DETAIL_HEADERS = ['Location', 'Counterperson', 'Pay Type', 'Sales', 'Cost', 'Gross', 'GP%'];
    wsDetail.getCell('A1').value = `Pay Type Detail · ${rangeLabel}`;
    wsDetail.getCell('A1').font = { size: 12, bold: true, color: { argb: 'FF1F2937' } };
    wsDetail.mergeCells(`A1:${wsDetail.getColumn(DETAIL_HEADERS.length).letter}1`);

    const detailHeaderRow = wsDetail.getRow(2);
    DETAIL_HEADERS.forEach((h, i) => {
      const cell = detailHeaderRow.getCell(i + 1);
      cell.value = h;
      headerStyle(cell, i >= 3);
    });
    detailHeaderRow.height = 20;

    let detailIdx = 3;
    for (const location of data.locations) {
      const locationName = getReportLocationName(location.locationId);
      for (const person of location.persons) {
        for (const { key, label } of PAY_TYPE_LABELS) {
          const metrics = person[key];
          if (metrics.sales === 0 && metrics.cost === 0 && metrics.gross === 0) continue;
          const r = wsDetail.getRow(detailIdx++);
          r.values = [
            locationName,
            person.employeeName,
            label,
            metrics.sales,
            metrics.cost,
            metrics.gross,
            metrics.gpPct ?? '',
          ];
          r.getCell(4).numFmt = money;
          r.getCell(5).numFmt = money;
          r.getCell(6).numFmt = money;
          r.getCell(7).numFmt = pct;
          for (let c = 4; c <= 7; c++) r.getCell(c).alignment = { horizontal: 'right' };
        }
      }
    }
    if (detailIdx > 3) {
      wsDetail.autoFilter = { from: { row: 2, column: 1 }, to: { row: detailIdx - 1, column: DETAIL_HEADERS.length } };
    }
    wsDetail.getColumn(1).width = 32;
    wsDetail.getColumn(2).width = 24;
    wsDetail.getColumn(3).width = 18;
    for (let c = 4; c <= 6; c++) wsDetail.getColumn(c).width = 13;
    wsDetail.getColumn(7).width = 8;

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Parts-Counterperson-Performance-${data.startMonth}_${data.endMonth}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setStatus('idle');
    setError(null);
    setReportData(null);
    setStartMonth(getDefaultStartMonth());
    setEndMonth(getDefaultEndMonth());
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleClose();
  };

  if (!isOpen) return null;

  const hasResults = status === 'complete';

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-4 print:p-0 print:overflow-visible print:static print:block"
      onClick={handleBackdropClick}
    >
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity print:hidden" />
      <div className="relative max-w-7xl w-full max-h-[95vh] bg-white rounded-lg shadow-xl flex flex-col print:max-w-none print:max-h-none print:rounded-none print:shadow-none print:block print:overflow-visible">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between print:border-b-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{report.title}</h2>
            <p className="text-sm text-gray-500 mt-1">All Locations • Parts</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 print:hidden"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 print:overflow-visible print:flex-none">
          <div className="mb-6 print:hidden">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Report Period</h3>
            <ReportMonthRangeSelector
              startMonth={startMonth}
              endMonth={endMonth}
              onStartMonthChange={setStartMonth}
              onEndMonthChange={setEndMonth}
              disabled={status === 'running'}
            />
            <p className="text-xs text-gray-400 mt-2">
              Data is monthly (nightly load); the current month reflects month-to-date as of the last load.
            </p>
          </div>

          <div className="mb-6 print:hidden">
            <button
              onClick={handleRunReport}
              disabled={!isValidRange || status === 'running'}
              className={`
                inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors
                ${!isValidRange || status === 'running'
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-orange-600 text-white hover:bg-orange-700'
                }
              `}
            >
              {status === 'running' ? (
                <>
                  <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {runningMessage}
                </>
              ) : (
                <>
                  <PlayIcon className="h-4 w-4 mr-2" />
                  Run Report
                </>
              )}
            </button>
          </div>

          <div className="border-t border-gray-200 mb-6 print:hidden" />

          <div id="report-print-content">
            <div className="hidden print:block mb-4">
              <h1 className="text-xl font-bold">Parts Counterperson Performance</h1>
              <p className="text-sm text-gray-600">
                All Locations • Parts • {formatMonthLabel(startMonth)}
                {endMonth !== startMonth ? ` to ${formatMonthLabel(endMonth)}` : ''}
              </p>
            </div>

            {status === 'idle' && (
              <div className="text-center py-12">
                <CircleStackIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">
                  Select a month range and click "Run Report" to see per-person parts counter totals across all locations.
                </p>
              </div>
            )}

            {status === 'running' && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-orange-600 mb-4" />
                <p className="text-gray-500">{runningMessage}</p>
              </div>
            )}

            {status === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error || 'An error occurred while generating the report.'}</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                >
                  Try again
                </button>
              </div>
            )}

            {status === 'complete' && reportData && (
              <PartsCounterReport data={reportData.data as PartsCounterReportData} />
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between print:hidden">
          <ReportExportButtons
            onPrintPDF={handlePrintPDF}
            onExportExcel={handleExportExcel}
            disabled={!hasResults}
          />
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
